import { useRef } from 'react';

const reduced = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * 한 줄씩 늘고 주는 입력 목록(Task List 항목 · 결과물 링크)의 줄 움직임.
 *   keys     — 줄마다 바뀌지 않는 key (지운 줄 아래가 한 칸씩 당겨져도 줄의 상태가 섞이지 않게)
 *   isFresh  — "+ Add" 로 방금 생긴 줄인지 (td-rise 로 떠오르게)
 *   add      — 새 줄을 붙인다
 *   remove   — 줄 높이를 0 으로 접은 뒤 지운다 (줄이 하나뿐이면 바로)
 *
 * @param rows     지금 그리는 줄 (문자열 배열)
 * @param onChange (next: string[]) => void
 * @param gap      줄 사이 간격(px) — 접힐 때 함께 없앤다
 */
const useRowMotion = (rows, onChange, gap = 8) => {
  const keysRef = useRef([]);
  const fresh = useRef(new Set());
  const seq = useRef(0);
  const pendingAdd = useRef(false);
  const latest = useRef({ rows, onChange });
  latest.current = { rows, onChange };

  // 바깥에서 목록이 통째로 바뀌어도(초기화 등) 줄 수에 맞춘다
  const keys = keysRef.current;
  while (keys.length < rows.length) {
    const k = `row-${(seq.current += 1)}`;
    if (pendingAdd.current) fresh.current.add(k);
    keys.push(k);
  }
  if (keys.length > rows.length) keys.length = rows.length;
  pendingAdd.current = false;

  const add = (value = '') => {
    pendingAdd.current = true;
    onChange([...rows, value]);
  };

  const drop = (key) => {
    const i = keysRef.current.indexOf(key);
    if (i < 0) return;
    keysRef.current.splice(i, 1);
    fresh.current.delete(key);
    const now = latest.current;
    now.onChange(now.rows.filter((_, j) => j !== i));
  };

  const remove = (i, el) => {
    const key = keysRef.current[i];
    if (!el?.animate || reduced() || rows.length <= 1) {
      drop(key);
      return;
    }
    el.style.overflow = 'hidden';
    el.style.pointerEvents = 'none';
    const anim = el.animate(
      [
        { height: `${el.offsetHeight}px`, opacity: 1, marginTop: '0px' },
        { height: '0px', opacity: 0, marginTop: `-${gap}px` },
      ],
      { duration: 200, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'forwards' },
    );
    anim.onfinish = () => drop(key);
  };

  return { keys, isFresh: (k) => fresh.current.has(k), add, remove };
};

export default useRowMotion;
